(function () {
  class FaqAccordionComponent extends HTMLElement {
    constructor() {
      super();

      // Elements
      this.faqItems = [];
      this.currentActiveItem = null;

      this.init();
    }

    init() {
      this.setupElements();
      this.bindEvents();
      this.setInitialState();
    }

    setupElements() {
      // Get all FAQ items
      this.faqItems = Array.from(this.querySelectorAll('[data-faq-item]'));
    }

    bindEvents() {
      // Add click event to each FAQ button
      this.faqItems.forEach((item) => {
        const button = item.querySelector('[data-faq-button]');
        if (button) {
          button.addEventListener('click', () => this.toggleItem(item));
        }
      });
    }

    setInitialState() {
      // Set first item as active by default
      if (this.faqItems.length > 0) {
        const firstItem = this.faqItems[0];
        this.currentActiveItem = firstItem;
        this.showItem(firstItem, false); // false = no animation on initial load
      }
    }

    /**
     * Toggle FAQ item
     * When clicking an item:
     * - If it's already active, collapse it
     * - If it's not active, collapse the currently active one and expand the new one
     */
    toggleItem(item) {
      const isActive = item.classList.contains('is-active');

      if (isActive) {
        // Collapse current item
        this.hideItem(item);
        this.currentActiveItem = null;
      } else {
        // Collapse currently active item (if any)
        if (this.currentActiveItem && this.currentActiveItem !== item) {
          this.hideItem(this.currentActiveItem);
        }

        // Expand new item
        this.showItem(item, true);
        this.currentActiveItem = item;
      }
    }

    /**
     * Show (expand) an item
     */
    showItem(item, animate = true) {
      if (!item) return;

      const button = item.querySelector('[data-faq-button]');
      const answer = item.querySelector('[data-faq-answer]');

      if (!answer) return;

      // Update aria-expanded
      if (button) {
        button.setAttribute('aria-expanded', 'true');
      }

      if (animate) {
        // Measure actual content height
        // Temporarily remove max-height constraint to measure actual height
        const currentMaxHeight = answer.style.maxHeight;
        const currentTransition = answer.style.transition;
        
        // Disable transition temporarily for measurement
        answer.style.transition = 'none';
        answer.style.maxHeight = 'none';
        
        // Get actual scroll height
        const actualHeight = answer.scrollHeight;
        
        // Immediately reset to initial state (before adding active class)
        answer.style.maxHeight = '0';
        answer.style.opacity = '0';
        answer.style.transition = currentTransition;

        // Add active class to trigger CSS transitions
        item.classList.add('is-active');

        // Use requestAnimationFrame to ensure smooth animation start
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Set target height and opacity
            answer.style.maxHeight = `${actualHeight}px`;
            answer.style.opacity = '1';
          });
        });

        // Clean up inline styles after transition completes
        const handleTransitionEnd = (e) => {
          if (e.target === answer && e.propertyName === 'max-height') {
            answer.style.maxHeight = '';
            answer.removeEventListener('transitionend', handleTransitionEnd);
          }
        };
        answer.addEventListener('transitionend', handleTransitionEnd);
      } else {
        // No animation - set directly for initial load
        item.classList.add('is-active');
        
        // Measure actual height without transition
        const currentTransition = answer.style.transition;
        answer.style.transition = 'none';
        answer.style.maxHeight = 'none';
        const actualHeight = answer.scrollHeight;
        
        // Set final state immediately
        answer.style.maxHeight = `${actualHeight}px`;
        answer.style.opacity = '1';
        answer.style.transition = currentTransition;
      }
    }

    /**
     * Hide (collapse) an item
     */
    hideItem(item) {
      if (!item) return;

      const button = item.querySelector('[data-faq-button]');
      const answer = item.querySelector('[data-faq-answer]');

      if (!answer) return;

      // Update aria-expanded
      if (button) {
        button.setAttribute('aria-expanded', 'false');
      }

      // Get current height before collapsing
      const currentHeight = answer.scrollHeight;
      answer.style.maxHeight = `${currentHeight}px`;

      // Force reflow to ensure the height is set
      answer.offsetHeight;

      // Remove active class to trigger CSS transition
      item.classList.remove('is-active');

      // Use requestAnimationFrame to ensure smooth animation start
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Set target state (collapsed)
          answer.style.maxHeight = '0';
          answer.style.opacity = '0';
        });
      });

      // Clean up inline styles after transition completes
      const handleTransitionEnd = (e) => {
        if (e.target === answer && e.propertyName === 'max-height') {
          if (!item.classList.contains('is-active')) {
            answer.style.maxHeight = '';
            answer.style.opacity = '';
          }
          answer.removeEventListener('transitionend', handleTransitionEnd);
        }
      };
      answer.addEventListener('transitionend', handleTransitionEnd);
    }

    disconnectedCallback() {
      // Clean up event listeners if needed
    }
  }

  customElements.define('faq-accordion-component', FaqAccordionComponent);

  // Handle back/forward cache
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      document.querySelectorAll('faq-accordion-component').forEach((component) => {
        if (component.setInitialState) {
          component.setInitialState();
        }
      });
    }
  });

  // FAQ deep linking via URL hash
  function openFaqFromHash() {
    const hash = window.location.hash;
    if (!hash) return;

    const target = document.querySelector(hash);
    if (!target) return;

    // Find the FAQ item containing the target (could be the item itself or an answer wrapper)
    const faqItem = target.closest('[data-faq-item]') || target.querySelector('[data-faq-item]');
    if (!faqItem) return;

    const component = faqItem.closest('faq-accordion-component');
    if (!component) return;

    // Collapse current active, expand target
    if (component.currentActiveItem && component.currentActiveItem !== faqItem) {
      component.hideItem(component.currentActiveItem);
    }
    component.showItem(faqItem, true);
    component.currentActiveItem = faqItem;

    setTimeout(function() {
      faqItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  }

  if (window.location.hash) {
    window.addEventListener('load', function() {
      setTimeout(openFaqFromHash, 500);
    });
  }
  window.addEventListener('hashchange', openFaqFromHash);
})();